import { Box, Stack } from "@mui/material";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { parseAsString, parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import { useActorsQuery } from "Actors/Api/Queries/useActorsQuery";
import {
  ACTORS_SORTABLE_COLUMNS,
  useActorsTableColumns,
} from "Actors/Components/useActorsTableColumns";
import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  ActorListOrderBy,
  ActorStatus,
  GenderType,
  type LanguageCodes,
  SortOrder,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { usePriceCategoryOptions } from "PriceCategories/Api/Queries/usePriceCategoryOptions";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import {
  ageFilterOptions,
  languageFilterOptions,
  sexFilterOptions,
} from "Shared/DataTable/filterOptions";
import { BlDropdown } from "Shared/Inputs/BlDropdown";

const SORT_COLUMN_MAP: Record<string, ActorListOrderBy> = {
  name: ActorListOrderBy.Name,
  language: ActorListOrderBy.Language,
  age: ActorListOrderBy.Age,
  price: ActorListOrderBy.Price,
};

export const ActorsTable = (): React.ReactNode => {
  const { t } = useResource();

  const [selectedLanguage, setSelectedLanguage] = useQueryState(
    "lang",
    parseAsString,
  );
  const [selectedAge, setSelectedAge] = useQueryState("age", parseAsString);
  const [selectedSex, setSelectedSex] = useQueryState(
    "sex",
    parseAsStringEnum<GenderType>(Object.values(GenderType)),
  );
  const [selectedPriceCategoryID, setSelectedPriceCategoryID] = useQueryState(
    "priceCategory",
    parseAsString,
  );
  const [selectedStatus, setSelectedStatus] = useQueryState(
    "status",
    parseAsStringEnum<ActorStatus>(Object.values(ActorStatus)),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
    sorting,
    onSortingChange,
  } = useTableSearchParams({ validSortIds: [...ACTORS_SORTABLE_COLUMNS] });

  const priceCategoryOptions = usePriceCategoryOptions();

  const effectivePriceCategoryID =
    selectedPriceCategoryID ||
    (priceCategoryOptions[0]?.value as string | undefined);

  const languages = selectedLanguage
    ? ([selectedLanguage] as LanguageCodes[])
    : undefined;
  const genders = selectedSex ? [selectedSex] : undefined;
  const ages = useMemo(() => {
    if (!selectedAge) return undefined;
    const [min, max] = selectedAge.split("-").map(Number);
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }, [selectedAge]);

  const orderBy = sorting[0]?.id ? SORT_COLUMN_MAP[sorting[0].id] : undefined;
  const sortOrder =
    sorting.length === 0
      ? undefined
      : sorting[0]?.desc
        ? SortOrder.Descending
        : SortOrder.Ascending;

  const statusOptions = [
    { value: "", label: t(Resources.Common.AllStatuses) },
    { value: ActorStatus.Active, label: t(Resources.Common.StatusActive) },
    { value: ActorStatus.Inactive, label: t(Resources.Common.StatusInactive) },
  ];

  const { data: actorsData, isLoading } = useActorsQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    searchQuery: debouncedSearch || null,
    languages,
    ages,
    genders,
    priceCategoryID: effectivePriceCategoryID,
    actorStatus: selectedStatus ? [selectedStatus] : null,
    orderBy,
    sortOrder,
  });

  const columns = useActorsTableColumns();

  const table = useReactTable({
    data: actorsData?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    rowCount: actorsData?.totalCount ?? 0,
    state: { sorting, pagination: { pageSize, pageIndex } },
    onSortingChange,
    onPaginationChange,
  });

  return (
    <Box>
      <Stack
        direction="row"
        spacing={Spacing.S}
        sx={{ marginBottom: Spacing.M }}
      >
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            name="actor-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Actors.ActorName)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="language-filter"
            options={languageFilterOptions()}
            value={selectedLanguage ?? ""}
            onChange={(value) => {
              setSelectedLanguage((value as string) || null);
              resetPage();
            }}
            placeholder={t(Resources.Actors.NativeLanguage)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="age-filter"
            options={ageFilterOptions()}
            value={selectedAge ?? ""}
            onChange={(value) => {
              setSelectedAge((value as string) || null);
              resetPage();
            }}
            placeholder={t(Resources.Actors.Age)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="sex-filter"
            options={sexFilterOptions()}
            value={selectedSex ?? ""}
            onChange={(value) => {
              setSelectedSex(value ? (value as GenderType) : null);
              resetPage();
            }}
            placeholder={t(Resources.Actors.Sex)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="price-category"
            options={priceCategoryOptions}
            value={effectivePriceCategoryID ?? ""}
            onChange={(value) => {
              setSelectedPriceCategoryID((value as string) || null);
            }}
            placeholder={t(Resources.Actors.PricePerModule)}
          />
        </Box>
        <Box sx={{ minWidth: 180 }}>
          <BlDropdown
            name="status-filter"
            options={statusOptions}
            value={selectedStatus ?? ""}
            onChange={(value) => {
              setSelectedStatus((value as ActorStatus) || null);
              resetPage();
            }}
            placeholder={t(Resources.Common.AllStatuses)}
          />
        </Box>
      </Stack>

      <BlTanstackTable table={table} isLoading={isLoading} />
    </Box>
  );
};
